import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopNavigation from 'components/ui/TopNavigation';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import PasswordField from './components/PasswordField';
import PasswordStrengthMeter, { getStrength } from './components/PasswordStrengthMeter';

function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-[300] flex items-center gap-3 px-4 py-3 rounded-[var(--radius-md)] shadow-[var(--shadow-xl)] text-white text-sm font-[var(--font-caption)] ${type === 'success' ? 'bg-[var(--color-success)]' : 'bg-[var(--color-error)]'
      }`}>
      <Icon name={type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} color="#fff" />
      {message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">
        <Icon name="X" size={14} color="#fff" />
      </button>
    </div>
  );
}

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [success, setSuccess] = useState(false);
  const [countdown, setCountdown] = useState(2);

  useEffect(() => {
    if (!success) return;
    const interval = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(interval);
          navigate('/profile-update-page');
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [success, navigate]);

  const validate = () => {
    const errs = {};
    if (!currentPassword) errs.currentPassword = 'Current password is required.';
    if (!newPassword) errs.newPassword = 'New password is required.';
    else if (getStrength(newPassword)?.score < 2) errs.newPassword = 'Password is too weak. Please meet more requirements.';
    if (!confirmPassword) errs.confirmPassword = 'Please confirm your new password.';
    else if (newPassword !== confirmPassword) errs.confirmPassword = 'Passwords do not match.';
    if (currentPassword && newPassword && currentPassword === newPassword) errs.newPassword = 'New password must differ from current password.';
    setErrors(errs);
    return Object.keys(errs)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { error } = await changePassword(currentPassword, newPassword);
      if (error) {
        if (error.message?.toLowerCase()?.includes('current')) {
          setErrors({ currentPassword: 'Current password is incorrect.' });
        } else {
          setToast({ message: error.message || 'Failed to change password. Please try again.', type: 'error' });
        }
        setSubmitting(false);
        return;
      }
      setSuccess(true);
      setToast({ message: 'Password changed successfully! Redirecting...', type: 'success' });
    } catch (err) {
      setToast({ message: err?.message || 'Failed to change password. Please try again.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      <TopNavigation />
      <main className="max-w-5xl mx-auto px-4 md:px-6 py-6 md:py-8">
        {/* Breadcrumb */}
        {/* <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-5">
          <Link to="/member-portal" className="hover:text-[var(--color-primary)] transition-colors">Member Portal</Link>
          <Icon name="ChevronRight" size={14} color="currentColor" />
          <Link to="/profile-update-page" className="hover:text-[var(--color-primary)] transition-colors">Profile Settings</Link>
          <Icon name="ChevronRight" size={14} color="currentColor" />
          <span className="text-[var(--color-foreground)]">Change Password</span>
        </div> */}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Change Password</h1>
            <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">Update your account password securely</p>
          </div>
          <button
            onClick={() => navigate('/profile-update-page')}
            className="flex items-center gap-2 px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--color-border)] text-sm text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:border-[var(--color-primary)]/40 transition-all font-[var(--font-caption)]"
          >
            <Icon name="ArrowLeft" size={14} color="currentColor" />
            Back
          </button>
        </div>

        {/* Card */}
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-sm)] p-6 md:p-8">
          {success ? (
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/10 flex items-center justify-center">
                <Icon name="ShieldCheck" size={32} color="var(--color-success)" />
              </div>
              <h2 className="text-lg font-bold text-[var(--color-foreground)] font-[var(--font-heading)]">Password Changed!</h2>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Your password has been updated successfully.
              </p>
              <p className="text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                Redirecting to Profile Settings in <span className="font-bold text-[var(--color-primary)]">{countdown}s</span>...
              </p>
              <button
                onClick={() => navigate('/profile-update-page')}
                className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity"
              >
                Go to Profile Settings
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" noValidate>
              {/* Security icon */}
              <div className="flex items-center gap-3 p-3 bg-[var(--color-primary)]/5 border border-[var(--color-primary)]/20 rounded-[var(--radius-sm)]">
                <Icon name="Shield" size={16} color="var(--color-primary)" />
                <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
                  For security, enter your current password before setting a new one.
                </p>
              </div>

              <PasswordField
                id="current-password"
                label="Current Password"
                value={currentPassword}
                onChange={setCurrentPassword}
                placeholder="Enter current password"
                error={errors?.currentPassword}
              />

              <div>
                <PasswordField
                  id="new-password"
                  label="New Password"
                  value={newPassword}
                  onChange={setNewPassword}
                  placeholder="Enter new password"
                  error={errors?.newPassword}
                />
                <PasswordStrengthMeter password={newPassword} />
              </div>

              <PasswordField
                id="confirm-password"
                label="Confirm New Password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Re-enter new password"
                error={errors?.confirmPassword}
              />

              {/* Match indicator */}
              {confirmPassword && newPassword && (
                <div className={`flex items-center gap-1.5 text-xs font-[var(--font-caption)] ${newPassword === confirmPassword ? 'text-[var(--color-success)]' : 'text-[var(--color-error)]'
                  }`}>
                  <Icon
                    name={newPassword === confirmPassword ? 'CheckCircle' : 'XCircle'}
                    size={13}
                    color="currentColor"
                  />
                  {newPassword === confirmPassword ? 'Passwords match' : 'Passwords do not match'}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-5 py-2.5 bg-[var(--color-primary)] text-white rounded-[var(--radius-sm)] text-sm font-medium font-[var(--font-caption)] hover:opacity-90 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              >
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Updating...</>
                ) : (
                  <><Icon name="KeyRound" size={14} color="#fff" />Update Password</>
                )}
              </button>
            </form>
          )}
        </div>
      </main>

      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}
